import { WIDGET_VERSION } from "@/constants";
import { globalLoggerToken } from "@/tokens";
import { AnalyticsEventsCollectorModule } from "@modules/analytics_events_collector/AnalyticsEventsCollectorModule";
import { inject, singleton } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";

import type { CustomizationData, WidgetConfig } from "./types";

@singleton()
class CustomizationManager {
    // Clone global customizations to preserve index stability
    private readonly customizations = (window.gw?.customizations || []).slice();

    private readonly cachedIndexes = new Map<string, number[]>();

    constructor(
        private readonly analyticsEventsCollectorModule: AnalyticsEventsCollectorModule,
        @inject(globalLoggerToken)
        private readonly logger: LoggerInterface
    ) {}

    private findCustomizationIndexes(config: WidgetConfig): number[] {
        const indexes: number[] = [];

        this.customizations.forEach(([selectWidget], index) => {
            if (selectWidget(config)) {
                indexes.push(index);

                this.analyticsEventsCollectorModule.exports.eventsCollector.pushEvent(
                    this.analyticsEventsCollectorModule.exports.eventsFactory.createWidgetCustomizationResolved(
                        config.widgetName
                    )
                );

                this.logger.debug(
                    "CustomizationManager.findCustomizationIndexes",
                    {
                        widget: config.widgetName,
                        customizationIndex: index,
                    }
                );
            }
        });

        return indexes;
    }

    private getCachedIndexes(
        config: WidgetConfig,
        resolver: () => number[]
    ): number[] {
        const existent = this.cachedIndexes.get(config.widgetName);

        if (existent) {
            return existent;
        }

        const resolved = resolver();
        this.cachedIndexes.set(config.widgetName, resolved);

        return resolved;
    }

    private getCustomizations(config: WidgetConfig): CustomizationData[] {
        const indexes = this.getCachedIndexes(config, () =>
            this.findCustomizationIndexes(config)
        );

        return indexes.map((index) => {
            const [, customizeWidget] = this.customizations[index];
            return customizeWidget(config);
        });
    }

    public injectStylesFromBlockCustomCss({
        element,
        shadowRoot,
        scriptSelector,
    }: {
        element: Element;
        shadowRoot: ShadowRoot | null;
        scriptSelector: string;
    }): void {
        const scriptElement =
            element.parentElement?.querySelector(scriptSelector);

        if (!scriptElement) return;

        const styleElement = document.createElement("style");
        styleElement.textContent = scriptElement.textContent;

        (shadowRoot || element).appendChild(styleElement);

        scriptElement.remove();

        this.logger.debug(
            "CustomizationManager.injectStylesFromBlockCustomCss",
            styleElement
        );
    }

    public injectStylesFromCustomization(config: WidgetConfig): void {
        const customizations = this.getCustomizations(config);

        if (customizations.length === 0) return;

        customizations.forEach((customization) => {
            if (!customization.styleElement) return;

            if (config.shadowRoot) {
                config.shadowRoot.appendChild(
                    customization.styleElement.cloneNode(true)
                );

                this.logger.debug(
                    "CustomizationManager.injectStylesFromCustomization",
                    customization.styleElement
                );
            }
        });
    }

    public async beforeWidgetInject(config: WidgetConfig): Promise<void> {
        const customizations = this.getCustomizations(config);

        if (customizations.length === 0) return;

        for (const customization of customizations) {
            const callback = customization.beforeWidgetInject;

            if (!callback) continue;

            const result = callback();

            if (result instanceof Promise) {
                await result;
            }

            this.logger.debug("CustomizationManager.beforeWidgetInject", {
                widget: config.widgetName,
            });
        }
    }

    public afterWidgetInject(config: WidgetConfig): void {
        const customizations = this.getCustomizations(config);

        if (customizations.length === 0) return;

        customizations.forEach((customization) => {
            const callback = customization.afterWidgetInject;

            if (!callback) return;

            callback();

            this.logger.debug("CustomizationManager.afterWidgetInject", {
                widget: config.widgetName,
            });
        });
    }

    public defineWidgetVersion(config: WidgetConfig): WIDGET_VERSION | null {
        const customizations = this.getCustomizations(config);

        if (customizations.length === 0) return null;

        // Take the last one, because it is the most relevant
        const lastCustomization = customizations[customizations.length - 1];
        const callback = lastCustomization.defineWidgetVersion;

        if (!callback) return null;

        const widgetVersion = callback();

        this.logger.debug("CustomizationManager.defineWidgetVersion", {
            widget: config.widgetName,
            version: widgetVersion,
        });

        return widgetVersion || null;
    }
}

export default CustomizationManager;
